import type { Agent, Persisted } from '@tunasong/schemas'
import { Menu, MenuContent, MenuTrigger } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC } from 'react'
import styles from './agent-pill.module.css'
import { AgentInitials, AgentRosterMenuItem } from './agent-roster-item.js'
import { useAgentRoster } from './use-agent-roster.js'

/** Initials shown stacked on the pill; the rest are in the menu. */
const maxStackedInitials = 3

export interface AgentPillProps {
  /** Host entity id (song / page / folder) whose agent children the pill lists. */
  parentId?: string | null
  /** Called when the user picks an agent from the menu. */
  onSelect: (agent: Persisted<Agent>) => void
  className?: string
}

/**
 * One header pill for every agent attached to the host: up to three stacked initials, the label
 * "Agents" and one unread count. The menu lists each agent; choosing one acknowledges its activity
 * (bumps the read cursor) and calls `onSelect`. Renders nothing without agents.
 */
export const AgentPill: FC<AgentPillProps> = ({ parentId, onSelect, className }) => {
  const { rows, unreadCount, isThinking, acknowledge } = useAgentRoster(parentId)

  if (rows.length === 0) {
    return null
  }

  const names = rows.map(row => row.name)
  const pillName = `Agents: ${names.join(', ')}${unreadCount > 0 ? `. ${unreadCount} unread` : ''}`

  const handleSelect = (agent: Persisted<Agent>) => {
    acknowledge(agent)
    onSelect(agent)
  }

  return (
    <Menu>
      <MenuTrigger
        render={
          <button type="button" className={cn(styles.pill, className)} aria-label={pillName}>
            <span className={cn(styles.stack, isThinking && styles['stack--thinking'])} aria-hidden="true">
              {rows.slice(0, maxStackedInitials).map(row => (
                <AgentInitials key={row.agent.id} row={row} className={styles.stacked} />
              ))}
            </span>
            <span className={styles.label}>Agents</span>
            {unreadCount > 0 ? (
              <span className={styles.badge} aria-hidden="true" data-unread-count={unreadCount}>
                {unreadCount}
              </span>
            ) : null}
          </button>
        }
      />
      <MenuContent>
        {rows.map(row => (
          <AgentRosterMenuItem key={row.agent.id} row={row} onSelect={handleSelect} />
        ))}
      </MenuContent>
    </Menu>
  )
}

export default AgentPill
